/**
 * Motion dice sounds and buzzes: a short synthesized clack per impact (no sound file: a burst of
 * filtered noise), as loud as the impact, at the sound effects volume. Haptics follow the same
 * impacts where the browser can vibrate (Options → Haptics).
 */
import { sfxLevel } from '../audio';

let ctx: AudioContext | null = null;
let noise: AudioBuffer | null = null;

function context(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  if (!ctx) {
    ctx = new AudioContext();
    noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.08), ctx.sampleRate);
    const data = noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3);
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  return ctx;
}

/** Call from a tap so later clacks may play (browsers block audio until the page is tapped). */
export function unlockSfx(): void {
  context();
}

let lastClack = 0;

/** A die hitting something; `hit` 0 to 1. `wood`: the tray (lower), else another die (sharper). */
export function clack(hit: number, wood: boolean): void {
  const level = sfxLevel() * Math.min(1, hit) * 0.8;
  const now = performance.now();
  if (level < 0.02 || now - lastClack < 25) return;
  lastClack = now;
  const c = context();
  if (!c || !noise) return;
  const src = c.createBufferSource();
  src.buffer = noise;
  src.playbackRate.value = 0.8 + Math.random() * 0.4;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = (wood ? 1400 : 3200) + Math.random() * 600;
  filter.Q.value = wood ? 3 : 6;
  const gain = c.createGain();
  gain.gain.value = level;
  src.connect(filter).connect(gain).connect(c.destination);
  src.start();
}

let lastBuzz = 0;

/** A short buzz for an impact (`hit` 0 to 1), at most one every 60 ms. */
export function thud(hit: number): void {
  const now = performance.now();
  if (hit < 0.15 || now - lastBuzz < 60 || typeof navigator === 'undefined' || !navigator.vibrate) return;
  lastBuzz = now;
  try {
    navigator.vibrate(Math.round(6 + 22 * Math.min(1, hit)));
  } catch {
    // Not allowed yet (no tap): a missed buzz isn't worth an error.
  }
}

/**
 * Dice shaken in a cup: one swing of the phone (`hit` 0 to 1) knocks them about, a few clacks
 * close together against the cup and each other.
 */
export function rattle(hit: number): void {
  const level = sfxLevel() * Math.min(1, hit) * 0.7;
  if (level < 0.02) return;
  const c = context();
  if (!c || !noise) return;
  const hits = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < hits; i++) {
    const wood = Math.random() < 0.4;
    const src = c.createBufferSource();
    src.buffer = noise;
    src.playbackRate.value = 0.8 + Math.random() * 0.5;
    const filter = c.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = (wood ? 1200 : 3000) + Math.random() * 900;
    filter.Q.value = wood ? 3 : 6;
    const gain = c.createGain();
    gain.gain.value = level * (0.5 + Math.random() * 0.5);
    src.connect(filter).connect(gain).connect(c.destination);
    src.start(c.currentTime + i * (0.012 + Math.random() * 0.02));
  }
}

/** The buzz for a swing of dice in a cup (`hit` 0 to 1): a knock and a rattle after it. */
export function shakeBuzz(hit: number): void {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  const h = Math.min(1, hit);
  lastBuzz = performance.now();
  try {
    navigator.vibrate([Math.round(14 + 26 * h), 18, Math.round(6 + 10 * h), 14, Math.round(4 + 8 * h)]);
  } catch {
    // Not allowed yet (no tap): a missed buzz isn't worth an error.
  }
}
